/**
 * The tickets a split made (YAG-1058), on the parent's sheet: one line per
 * child in build order, with the repository it builds in, where it stands,
 * its pull request, and what it waits on. Read once per parent
 * (`GET /api/work-items/:id/children`), only for a ticket that was split.
 */
import { useEffect, useState } from "react";
import { type WorkItem, type WorkItemSplitChild, workItemUrl } from "@yagni/shared";
import { Fold } from "../queue/index.js";
import { captureUiError } from "../sentry.js";
import { phaseLabel } from "../work/workAtoms.js";
import { PrChip } from "../work/PrChip.js";
import {
  readSplitPlace,
  splitMergeWords,
  splitParentName,
  splitPositionWords,
  splitWaitWords,
} from "../work/splitPlace.js";

export const SPLIT_CHILDREN_TITLE = "Tickets of this split";
export const SPLIT_CHILD_WAITS = "waits for the one before it to merge";
/** Where an ordered child shows its number, a documentation child built beside the code (YAG-1067). */
export const SPLIT_CHILD_BESIDE = "Beside";

/** The split's tickets, or null while reading and for a ticket that was never split. */
export function useSplitChildren(
  workItemId: string,
  enabled: boolean,
): WorkItemSplitChild[] | null {
  const [children, setChildren] = useState<WorkItemSplitChild[] | null>(null);
  useEffect(() => {
    setChildren(null);
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}/children`, {
          credentials: "include",
        });
        if (res.status === 404) return;
        if (!res.ok) throw new Error(`read_split_children failed (${res.status})`);
        const body = (await res.json()) as { children?: WorkItemSplitChild[] };
        if (!cancelled) setChildren(Array.isArray(body.children) ? body.children : []);
      } catch (err) {
        captureUiError(err, { area: "Work", operation: "read_split_children" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemId, enabled]);
  return children;
}

/** The address the parent's Team queue opens a child at. */
function childHref(deskId: string | null, child: WorkItemSplitChild): string | null {
  return deskId
    ? `/desks/${encodeURIComponent(deskId)}?item=${encodeURIComponent(child.id)}`
    : null;
}

/** "waits for gpx/api to merge", naming the sibling it waits on when the list has it. */
export function splitChildWaitWords(
  child: WorkItemSplitChild,
  siblings: readonly WorkItemSplitChild[],
): string {
  const before = siblings.find((sibling) => sibling.id === child.waitsOn);
  const name = before?.repoFullName ?? before?.title;
  return name ? `waits for ${name} to merge` : SPLIT_CHILD_WAITS;
}

/** "merges after gpx/api": a child built ahead, whose merge waits on the sibling before it (YAG-1112). */
export function splitChildMergeWords(
  child: WorkItemSplitChild,
  siblings: readonly WorkItemSplitChild[],
): string {
  const before = siblings.find((sibling) => sibling.id === child.waitsOn);
  return `merges after ${before?.repoFullName ?? before?.title ?? "the one before it"}`;
}

function ChildLine({
  child,
  siblings,
  deskId,
  onOpen,
}: {
  child: WorkItemSplitChild;
  siblings: readonly WorkItemSplitChild[];
  deskId: string | null;
  onOpen?: (id: string) => void;
}) {
  const href = childHref(deskId, child);
  const state = phaseLabel({ phase: child.phase } as WorkItem);
  return (
    <li data-testid="ticket-split-child">
      {child.beside ? (
        <strong>{SPLIT_CHILD_BESIDE}: </strong>
      ) : (
        child.order !== null && <strong>{child.order}. </strong>
      )}
      {href && onOpen ? (
        <a
          href={href}
          onClick={(event) => {
            event.preventDefault();
            onOpen(child.id);
          }}
        >
          {child.title}
        </a>
      ) : (
        child.title
      )}
      {child.repoFullName && (
        <>
          {" "}
          · <code>{child.repoFullName}</code>
        </>
      )}{" "}
      · {child.waitsOn && !child.ahead ? splitChildWaitWords(child, siblings) : state}
      {child.waitsOn && child.ahead && <> · {splitChildMergeWords(child, siblings)}</>}
      {child.prUrl && (
        <>
          {" "}
          · <PrChip prUrl={child.prUrl} prNumber={child.prNumber} />
        </>
      )}
    </li>
  );
}

export const SPLIT_PLACE_LABEL = "Part of a split";

/**
 * On a split's ticket (ENG-3033): the ticket it came out of, linked, its
 * place in the order, and what it waits on to merge. Nothing for a ticket no
 * split made.
 */
export function SplitPlaceBlock({
  item,
  onOpen,
}: {
  item: Pick<WorkItem, "sourceRef" | "deskId">;
  /** Opens the parent in the sheet; without it, the link goes to the parent's address. */
  onOpen?: (id: string) => void;
}) {
  const place = readSplitPlace(item);
  if (!place) return null;
  const href = item.deskId ? workItemUrl(place.parentId, item.deskId) : null;
  const name = splitParentName(place);
  const wait = splitWaitWords(place);
  const merge = splitMergeWords(place);
  return (
    <div className="blk" data-testid="ticket-split-place">
      <div className="bh">
        <span className="label">{SPLIT_PLACE_LABEL}</span>
        {splitPositionWords(place) && <span className="fact">{splitPositionWords(place)}</span>}
      </div>
      <p className="md-body brief">
        Split from{" "}
        {href ? (
          <a
            href={href}
            data-testid="ticket-split-parent"
            onClick={(event) => {
              if (!onOpen) return;
              event.preventDefault();
              onOpen(place.parentId);
            }}
          >
            {name}
          </a>
        ) : (
          name
        )}
        .{wait && ` This ticket ${wait}, then its plan starts on its own.`}
        {merge && ` Its plan and build start now, and its pull request ${merge}.`}
      </p>
    </div>
  );
}

/** A ticket a split made tickets from: split now, or done once they all merged (YAG-1111). */
export function wasSplit(item: Pick<WorkItem, "phase" | "splitProgress">): boolean {
  return item.phase === "split" || item.splitProgress != null;
}

/** The split's tickets, folded on the parent; nothing for a ticket that was never split. */
export function SplitChildrenFold({
  item,
  defaultOpen,
  onOpen,
}: {
  item: Pick<WorkItem, "id" | "phase" | "deskId" | "splitProgress">;
  defaultOpen?: boolean;
  /** Opens a child in the sheet; without it, the line links to the child's address. */
  onOpen?: (id: string) => void;
}) {
  const children = useSplitChildren(item.id, wasSplit(item));
  if (!children || children.length === 0) return null;
  return (
    <Fold title={SPLIT_CHILDREN_TITLE} count={children.length} defaultOpen={defaultOpen}>
      <div className="md-body brief" data-testid="ticket-split-children">
        <ol>
          {children.map((child) => (
            <ChildLine
              key={child.id}
              child={child}
              siblings={children}
              deskId={item.deskId}
              onOpen={onOpen}
            />
          ))}
        </ol>
      </div>
    </Fold>
  );
}
